'use client'

import { memo } from 'react'
import { cn } from '@/lib/tailwind'
import { formatRelativeTime } from '@/lib/date'
import { presentationFor } from '@/entries'
import type { Entry } from '@/document'

/**
 * Normalize cosine similarity to a display percentage.
 * EmbeddingGemma typical range: 0.10 (noise) – 0.50 (strong match).
 * Maps: 0.10 → 0%, 0.50+ → 99%.
 */
function formatRelevance(rawScore: number): string {
  const MIN = 0.1
  const MAX = 0.5
  const normalized = Math.max(0, Math.min(1, (rawScore - MIN) / (MAX - MIN)))
  return `${Math.round(normalized * 100)}%`
}

function stripMarkdown(text: string): string {
  return text
    .replace(/[#*_~`>\-\[\]()!]/g, '')
    .replace(/\n+/g, ' ')
    .trim()
}

interface SearchResultRowProps {
  entry: Entry
  score?: number
  selected: boolean
  onClick: () => void
}

export const SearchResultRow = memo(function SearchResultRow({
  entry,
  score,
  selected,
  onClick,
}: SearchResultRowProps) {
  const config = presentationFor(entry.type)
  const Icon = config.icon
  const stripped = entry.content ? stripMarkdown(entry.content) : ''
  const snippet =
    stripped.length > 120 ? stripped.slice(0, 120) + '...' : stripped

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-left',
        'transition-colors duration-100',
        'focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none',
        selected ? 'bg-muted/60' : 'hover:bg-muted/40',
      )}
      style={{ minHeight: 60 }}
    >
      <div className="text-muted-foreground flex h-8 w-8 shrink-0 items-center justify-center rounded-md">
        <Icon className="h-4 w-4" weight="regular" />
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground bg-muted/80 rounded px-1.5 py-0.5 text-[10px] font-medium tracking-wider uppercase">
            {config.label}
          </span>
          {score !== undefined && (
            <span className="text-muted-foreground/60 text-[10px] tabular-nums">
              {formatRelevance(score)}
            </span>
          )}
        </div>
        <p className="text-foreground mt-0.5 truncate text-sm font-medium">
          {entry.title || 'Untitled'}
        </p>
        {snippet && (
          <p className="text-muted-foreground mt-0.5 truncate text-xs">
            {snippet}
          </p>
        )}
      </div>

      <span className="text-muted-foreground shrink-0 text-xs">
        {formatRelativeTime(entry.createdAt)}
      </span>
    </button>
  )
})
